import EventEmitter from "./event-emitter";
import FormFieldController from "./form-field-controller";
import FormEventSubscription from "./form-event-subscription";
import {FormCacheStorageInterface} from "./form-cache-storage";
import {compareMaps} from "../../../lib";
import Api from "../../../lib/api";

export default class FormController {
    constructor(props = {}) {
        this.id = props.id;
        this.baseUrl = props.baseUrl;
        this.path = props.path;
        this.data = props.data || {};
        this.submitData = props.submitData || {};
        this.fieldControllers = props.fieldControllers;
        this.guards = props.guards || [];
        this.subscriptions = props.subscriptions || [];
        this.cacheStorage = props.cacheStorage || null;
        this.cacheEnabled = props.cacheEnabled || false;
        this.submitDataTransformer = props.submitDataTransformer || null;

        this.valid = false;

        this._value = new Map();
        this._eventEmitter = null;

        this.VALUE_CHANGE_EVENT = 'valueChange';
        this.VALIDATION_STATUS_CHANGE_EVENT = 'validationStatusChange';
        this.FIELD_VALUE_CHANGE_EVENT = 'fieldValueChange';

        this.BEFORE_SUBMIT_EVENT = 'beforeSubmit';
        this.SUBMIT_COMPLETE_EVENT = 'submitComplete';
        this.SUBMIT_SUCCESS_EVENT = 'submitSuccess';
        this.SUBMIT_FAILURE_EVENT = 'submitFailure';

        this.init();
    }

    get eventEmitter() {
        return this._eventEmitter;
    }

    init() {
        this._checkProps();
        this._initEventEmitter();
        this._initData();
        this._initFields();
    }

    getFieldValue(name) {
        return this._value.get(name);
    }

    validate() {
        this.fieldControllers.forEach((field) => field.validate());
    }

    updateData(data) {
        if (!data || typeof data !== 'object') {
            return;
        }

        Object.assign(this.submitData, data);
    }

    getSubmitData() {
        const submitBody = Object.fromEntries(this._value);
        Object.assign(submitBody, this.submitData);

        const data = {
            url: `${this.baseUrl}${this.path}`,
            body: submitBody,
        };

        if (this.submitDataTransformer && typeof this.submitDataTransformer === 'function') {
            return this.submitDataTransformer(data);
        }

        return data;
    }

    submit(promise) {
        if (!this.valid || !this._getGuardsStatus()) {
            return;
        }

        let submitData = this.getSubmitData();
        this._eventEmitter.dispatch(this.BEFORE_SUBMIT_EVENT, submitData);
        submitData = this.getSubmitData();

        return Promise.allSettled([
            Api.post(submitData.url, submitData.body, { withCredentials: true }),
            ...(promise ? [promise] : [])
        ]).then((result) => {
            return result[0];
        }).then((response) => {
            if (response.status === 'fulfilled') {
                this._eventEmitter.dispatch(this.SUBMIT_SUCCESS_EVENT, response.value);
                this.cacheValues();
            } else {
                this._eventEmitter.dispatch(this.SUBMIT_FAILURE_EVENT, response.reason);
            }

            this._eventEmitter.dispatch(this.SUBMIT_COMPLETE_EVENT, response);
        });
    }

    cacheValues() {
        if (!this.cacheStorage || !this.cacheEnabled) {
            return;
        }

        const cacheData = this.fieldControllers.reduce((acc, field) => {
            if (field.cacheable) {
                acc[field.name] = field.value;
            }
            return acc;
        }, {});

        this.cacheStorage.batchSet(cacheData);
    }

    setInitialValue() {
        this.fieldControllers.forEach((field) => field.setInitialValue(null));
    }

    _checkProps() {
        if (!this.id) {
            throw new Error('FormController: ID is not defined!');
        }

        if (!this.baseUrl) {
            throw new Error('FormController: Base URL is not defined!');
        }

        if (!this.path) {
            throw new Error('FormController: Path is not defined!');
        }

        if (!this.fieldControllers || !Array.isArray(this.fieldControllers) || !this.fieldControllers.length) {
            throw new Error('FormController: Field controllers are not defined!');
        }

        if (this.fieldControllers.some((field) => !(field instanceof FormFieldController))) {
            throw new Error('FormController: Field controllers must be instances of FormFieldController!');
        }

        if (this.guards.some((guard) => typeof guard !== 'function')) {
            throw new Error('FormController: Guards must be functions!');
        }

        if (this.subscriptions.some((subscription) => !(subscription instanceof FormEventSubscription))) {
            throw new Error('FormController: Subscriptions must be instances of FormEventSubscription!');
        }

        if (this.cacheStorage && !(this.cacheStorage instanceof FormCacheStorageInterface)) {
            throw new Error('FormController: Cache storage must be an instance of FormCacheStorageInterface!');
        }
    }

    _initEventEmitter() {
        this._eventEmitter = new EventEmitter(this.subscriptions.filter((subscription) => !subscription.field));
    }

    _initData() {
        this._value = new Map(Object.entries(this.data));
    }

    _initFields() {
        for (const field of this.fieldControllers) {
            this._initField(field);
        }
    }

    _initField(field) {
        field.subscribe(field.VALUE_CHANGE_EVENT, (value) => {
            if (!field.unsendable) {
                this._setNamedValue(field.name, value);
            }
            this._eventEmitter.dispatch(this.FIELD_VALUE_CHANGE_EVENT, { name: field.name, value });
        });

        field.subscribe(field.VALIDATION_STATUS_CHANGE_EVENT, () => {
            this.valid = this._getValidationStatus();
            this._eventEmitter.dispatch(this.VALIDATION_STATUS_CHANGE_EVENT, this.valid);
        });

        field.addSubscriptions(this.subscriptions.filter((subscription) => subscription.field === field.name));

        if (this.data.hasOwnProperty(field.name)) {
            field.setInitialValue(this.data[field.name]);
        } else if (this.cacheStorage && this.cacheEnabled && field.cacheable) {
            field.setInitialValue(this.cacheStorage.get(field.name));
        } else if (field.value) {
            this._setNamedValue(field.name, field.value, { dispatchEvents: false });
        }
    }

    _getValidationStatus() {
        return this.fieldControllers.every((field) => field.valid);
    }

    _getGuardsStatus() {
        if (!this.guards.length) {
            return true;
        }

        return this.guards.every((guard) => guard(this._value));
    }

    _setValue(value, options = {}) {
        if (!value || !(value instanceof Map)) {
            throw new Error('FormController: Value must be an instance of Map!');
        }

        const dispatchEvents = options.dispatchEvents !== false;

        if (compareMaps(this._value, value)) {
            return;
        }

        this._value = value;

        if (dispatchEvents) {
            this._eventEmitter.dispatch(this.VALUE_CHANGE_EVENT, this._value);
        }
    }

    _setNamedValue(name, value, options = {}) {
        const newValue = new Map(this._value);
        newValue.set(name, value);
        this._setValue(newValue, options);
    }
}